/**
 * CSV Parser — parse CSV text into package lines
 */

export interface CSVParseResult {
  lines: CSVRow[];
  errors: string[];
  warnings: string[];
}

export interface CSVRow {
  length?: number;
  width?: number;
  height?: number;
  weight?: number;
  quantity?: number;
  dimUnit?: string;
  weightUnit?: string;
  raw: string[];
}

const REQUIRED_HEADERS = ['length', 'width', 'height', 'weight'] as const;

function normalize(header: string): string {
  return header.trim().toLowerCase().replace(/[_\s-]+/g, '');
}

const HEADER_MAP: Record<string, keyof CSVRow> = {
  length: 'length',
  len: 'length',
  l: 'length',
  width: 'width',
  w: 'width',
  height: 'height',
  h: 'height',
  weight: 'weight',
  wt: 'weight',
  qty: 'quantity',
  quantity: 'quantity',
  count: 'quantity',
  dimunit: 'dimUnit',
  dim_unit: 'dimUnit',
  unit: 'dimUnit',
  weightunit: 'weightUnit',
  weight_unit: 'weightUnit',
  wgtunit: 'weightUnit',
};

export function detectColumnMapping(headers: string[]): {
  mapping: Record<number, keyof CSVRow>;
  errors: string[];
} {
  const mapping: Record<number, keyof CSVRow> = {};
  const errors: string[] = [];
  const found = new Set<string>();

  for (let i = 0; i < headers.length; i++) {
    const norm = normalize(headers[i]!);
    const field = HEADER_MAP[norm];
    if (field && !found.has(field)) {
      mapping[i] = field;
      found.add(field);
    }
  }

  for (const req of REQUIRED_HEADERS) {
    if (!found.has(req)) {
      errors.push(`Missing required column: ${req}`);
    }
  }

  return { mapping, errors };
}

function parseNumber(val: string): number | undefined {
  const cleaned = val.trim().replace(/,/g, '');
  if (cleaned === '' || cleaned === '-') return undefined;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

export function parseCSV(csvText: string): CSVParseResult {
  const lines = csvText
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  const errors: string[] = [];
  const warnings: string[] = [];

  if (lines.length < 2) {
    return { lines: [], errors: ['CSV must have at least a header row and one data row'], warnings };
  }

  // Parse header
  const headerLine = lines[0]!;
  const headers = splitCSVLine(headerLine);
  const { mapping, errors: mappingErrors } = detectColumnMapping(headers);
  errors.push(...mappingErrors);

  if (errors.length > 0) {
    return { lines: [], errors, warnings };
  }

  const results: CSVRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const fields = splitCSVLine(lines[i]!);
    const row: CSVRow = { raw: fields };

    for (const [colIdx, field] of Object.entries(mapping)) {
      const val = fields[Number(colIdx)]?.trim() ?? '';
      if (field === 'dimUnit' || field === 'weightUnit') {
        (row as unknown as Record<string, string | undefined>)[field] = val || undefined;
      } else if (field !== 'raw') {
        const n = parseNumber(val);
        if (n !== undefined) {
          (row as unknown as Record<string, number | undefined>)[field] = n;
        } else if (val !== '') {
          warnings.push(`Row ${i + 1}: Could not parse "${val}" as number for ${field}`);
        }
      }
    }

    // Validate required fields
    if (row.length === undefined && row.width === undefined && row.height === undefined && row.weight === undefined) {
      warnings.push(`Row ${i + 1}: All dimension/weight fields empty — skipping`);
      continue;
    }

    results.push(row);
  }

  return { lines: results, errors, warnings };
}

function splitCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
      } else if (ch === ',') {
        result.push(current);
        current = '';
      } else {
        current += ch;
      }
    }
  }

  result.push(current);
  return result;
}
